import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  PointerEvent as ReactPointerEvent,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react'

/** Matches Safari/Chrome's press-and-hold delay before the history menu appears. */
const LONG_PRESS_MS = 400

interface UseLongPressOptions {
  onLongPress: () => void
  /** Fires on a short press — the plain click the control normally performs. */
  onClick: () => void
  disabled?: boolean
  delayMs?: number
}

/**
 * Press-and-hold *or* right-click, on one control, without the two racing.
 *
 * The subtlety is the trailing `click`: a pointerup after a long press still
 * fires one, so without suppression a hold would open the menu and immediately
 * navigate. We therefore drive the click from `pointerup` ourselves and swallow
 * the native one, rather than letting both paths run.
 */
export function useLongPress({ onLongPress, onClick, disabled, delayMs = LONG_PRESS_MS }: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firedRef = useRef(false)
  const [isPressed, setIsPressed] = useState(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // A press interrupted by unmount would otherwise leave the timer to fire
  // against a torn-down tree.
  useEffect(() => clearTimer, [clearTimer])

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // Secondary buttons are the context-menu path; don't start a hold too.
      if (disabled || event.button !== 0) return
      firedRef.current = false
      setIsPressed(true)
      clearTimer()
      timerRef.current = setTimeout(() => {
        firedRef.current = true
        setIsPressed(false)
        onLongPress()
      }, delayMs)
    },
    [clearTimer, delayMs, disabled, onLongPress],
  )

  const handlePointerUp = useCallback(() => {
    clearTimer()
    setIsPressed(false)
    if (disabled || firedRef.current) return
    onClick()
  }, [clearTimer, disabled, onClick])

  // Leaving the control cancels the hold *and* the click, so a drag-off is an
  // escape hatch rather than a navigation.
  const handlePointerLeave = useCallback(() => {
    clearTimer()
    setIsPressed(false)
    firedRef.current = true
  }, [clearTimer])

  const handleContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault()
      if (disabled) return
      clearTimer()
      firedRef.current = true
      setIsPressed(false)
      onLongPress()
    },
    [clearTimer, disabled, onLongPress],
  )

  /**
   * A hold is unreachable from the keyboard, so the menu needs its own keys or
   * the control's `aria-haspopup` would promise something keyboard users can't
   * open. Alt+Down and Shift+F10 are the standard "open the menu for this
   * control" bindings (WAI-ARIA APG); Enter/Space stay the plain navigation.
   */
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (disabled) return
      const wantsMenu =
        (event.altKey && event.key === 'ArrowDown') ||
        (event.shiftKey && event.key === 'F10') ||
        event.key === 'ContextMenu'
      if (wantsMenu) {
        event.preventDefault()
        onLongPress()
        return
      }
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onClick()
    },
    [disabled, onClick, onLongPress],
  )

  return {
    isPressed,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerLeave: handlePointerLeave,
      onContextMenu: handleContextMenu,
      onKeyDown: handleKeyDown,
      // The native click is fully replaced by the pointerup path above.
      onClick: (event: ReactMouseEvent<HTMLElement>) => event.preventDefault(),
    },
  }
}
